import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";
import { toast } from 'react-toastify';
import { mergeActionUpdate } from "../../pages/NavPages/DailyDigestPage/digestHelpers";

// Only these three may be sent as `status` — the server rejects anything else with 400.
// ActionCard's Approve/Skip buttons dispatch DIGEST_ACTION_STATUSES.APPROVED /
// .SKIPPED rather than hardcoding the strings, so this stays the one source of truth.
export const DIGEST_ACTION_STATUSES = {
    PROPOSED: 'proposed',
    APPROVED: 'approved',
    SKIPPED: 'skipped'
};

export const fetchTodayDigest = createAsyncThunk("digest/fetchTodayDigest", async (_, thunkAPI) => {
    try {
        const response = await api.get('/api/agent/digest/today');
        return response.data?.digest || null;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.msg || err.response?.data?.message || err.response?.data?.error || err.message);
    }
});


// Past digests, summarised — the record of what the agent proposed and what the
// user did about it. Kept separate from `digest` so opening History never
// disturbs today's working state.
export const fetchDigestHistory = createAsyncThunk("digest/fetchDigestHistory", async (_, thunkAPI) => {
    try {
        const response = await api.get('/api/agent/digest/history');
        return response.data?.history || [];
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.msg || err.response?.data?.message || err.response?.data?.error || err.message);
    }
});

// Persists an approve/skip decision and/or an edited draft for one action.
// The server returns the updated action, which the fulfilled reducer splices
// into digest.actions directly — no full-digest refetch per edit.
export const updateAction = createAsyncThunk("digest/updateAction",
    async ({ digestId, actionId, status, draft, contactsRequested }, thunkAPI) => {
        try {
            const body = {};
            if (status !== undefined) body.status = status;
            if (draft !== undefined) body.draft = draft;
            // Only valid on a fetch_contact action; the server 400s it on any
            // other kind, so callers (the per-row contact picker) only ever
            // send this alongside a fetch_contact actionId.
            if (contactsRequested !== undefined) body.contactsRequested = contactsRequested;
            const response = await api.post(`/api/agent/digest/${digestId}/actions/${actionId}`, body);
            return response.data?.action || null;
        } catch (err) {
            const msg = err.response?.data?.msg || err.response?.data?.message || err.response?.data?.error || err.message;
            toast.error(msg || "Failed to update action.");
            return thunkAPI.rejectWithValue(msg);
        }
    });

// Marks an action as manually sent by the user (used from the Connect-required
// banner — Copy + Mark as sent — so an unconnected channel is never a dead end).
export const markActionSent = createAsyncThunk("digest/markActionSent",
    async ({ digestId, actionId }, thunkAPI) => {
        try {
            const response = await api.post(`/api/agent/digest/${digestId}/actions/${actionId}/mark-sent`);
            toast.success("Marked as sent.");
            return response.data?.action || null;
        } catch (err) {
            const msg = err.response?.data?.msg || err.response?.data?.message || err.response?.data?.error || err.message;
            toast.error(msg || "Failed to mark as sent.");
            return thunkAPI.rejectWithValue(msg);
        }
    });

/**
 * Write the real draft for one row.
 *
 * Drafting is lazy — a digest of 100 rows does not draft them all up front, so
 * this fills one in the moment the user actually looks at it.
 */
export const draftAction = createAsyncThunk("digest/draftAction",
    async ({ digestId, actionId, force = false, userPrompt = '' }, thunkAPI) => {
        try {
            const response = await api.post(
                `/api/agent/digest/${digestId}/actions/${actionId}/draft`,
                { force, userPrompt }
            );
            return response.data?.action || null;
        } catch (err) {
            const msg = err.response?.data?.msg || err.response?.data?.message || err.message;
            toast.error(msg || "Could not write this draft.");
            return thunkAPI.rejectWithValue(msg);
        }
    });

/**
 * Send one row now, from the user's own connected account.
 *
 * Budget-checked server-side: a 429 here means a real LinkedIn or Gmail limit
 * was reached, not that the app is throttling for its own convenience.
 */
export const sendActionNow = createAsyncThunk("digest/sendActionNow",
    async ({ digestId, actionId }, thunkAPI) => {
        try {
            const response = await api.post(`/api/agent/digest/${digestId}/actions/${actionId}/send`);
            toast.success("Sent.");
            return response.data?.action || null;
        } catch (err) {
            const msg = err.response?.data?.msg || err.response?.data?.message || err.message;
            toast.error(msg || "Send failed.");
            return thunkAPI.rejectWithValue(msg);
        }
    });

/**
 * Re-read the current digest in place, silently, after a send.
 *
 * A whole-digest read is where "already emailed / invited" and the corrected
 * "why now" are computed, so this is how a row catches up with a send made a
 * moment ago. It uses GET /digest/:id rather than /digest/today: no loading
 * state (the page never flashes its loader), and it is not under the tight
 * /digest/today rate limit, so sending several rows in a row cannot trip it.
 * Failures are ignored — the row on screen is still correct, only less fresh.
 */
export const refreshDigest = createAsyncThunk("digest/refreshDigest",
    async (digestId, thunkAPI) => {
        try {
            const response = await api.get(`/api/agent/digest/${digestId}`);
            return response.data?.digest || null;
        } catch (err) {
            return thunkAPI.rejectWithValue(err.message);
        }
    });

export const regenerateDigest = createAsyncThunk("digest/regenerateDigest", async (_, thunkAPI) => {
    try {
        const response = await api.post('/api/agent/digest/regenerate');
        return response.data?.digest || null;
    } catch (err) {
        const msg = err.response?.data?.msg || err.response?.data?.message || err.response?.data?.error || err.message;
        toast.error(msg || "Failed to regenerate digest.");
        return thunkAPI.rejectWithValue(msg);
    }
});

// Spends real credits: fetches contacts for every approved fetch_contact row.
// Returns { attempted, succeeded, failed, contactsFetched, creditsSpent, results }
// so the page can toast a concrete outcome instead of a bare "done".
export const executeDigest = createAsyncThunk("digest/executeDigest",
    async ({ digestId }, thunkAPI) => {
        try {
            const response = await api.post(`/api/agent/digest/${digestId}/execute`);
            return response.data?.summary || null;
        } catch (err) {
            const msg = err.response?.data?.msg || err.response?.data?.message || err.response?.data?.error || err.message;
            toast.error(msg || "Failed to execute digest.");
            return thunkAPI.rejectWithValue(msg);
        }
    });

export const fetchAgentSettings = createAsyncThunk("digest/fetchAgentSettings", async (_, thunkAPI) => {
    try {
        const response = await api.get('/api/agent/settings');
        return response.data || {};
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.msg || err.response?.data?.message || err.response?.data?.error || err.message);
    }
});

export const saveAgentSettings = createAsyncThunk("digest/saveAgentSettings", async (settingsPatch, thunkAPI) => {
    try {
        const response = await api.put('/api/agent/settings', settingsPatch);
        toast.success("Settings saved.");
        return response.data || {};
    } catch (err) {
        const msg = err.response?.data?.msg || err.response?.data?.message || err.response?.data?.error || err.message;
        toast.error(msg || "Failed to save settings.");
        return thunkAPI.rejectWithValue(msg);
    }
});

const replaceActionInDigest = (state, action) => {
    if (!action || !state.digest) return;
    const idx = (state.digest.actions || []).findIndex((a) => a.actionId === action.actionId);
    if (idx === -1) return;
    state.digest.actions[idx] = mergeActionUpdate(state.digest.actions[idx], action);
};

const digestSlice = createSlice({
    name: "digest",
    initialState: {
        digest: null,
        settings: null,
        creditsBalance: null,
        channels: null,
        history: [],
        historyLoading: false,
        loading: false,
        executing: false,
        lastExecuteSummary: null,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // FETCH: today's digest
            .addCase(fetchTodayDigest.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTodayDigest.fulfilled, (state, action) => {
                state.loading = false;
                state.digest = action.payload;
            })
            .addCase(fetchTodayDigest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(refreshDigest.fulfilled, (state, action) => {
                // Only if the user is still looking at the same digest.
                if (action.payload && state.digest && String(action.payload._id) === String(state.digest._id)) {
                    state.digest = action.payload;
                }
            })

            // FETCH: past digests
            .addCase(fetchDigestHistory.pending, (state) => {
                state.historyLoading = true;
            })
            .addCase(fetchDigestHistory.fulfilled, (state, action) => {
                state.historyLoading = false;
                state.history = action.payload;
            })
            .addCase(fetchDigestHistory.rejected, (state, action) => {
                state.historyLoading = false;
                state.error = action.payload;
            })

            // UPDATE: one action's status/draft
            .addCase(updateAction.fulfilled, (state, action) => {
                replaceActionInDigest(state, action.payload);
            })
            .addCase(updateAction.rejected, (state, action) => {
                state.error = action.payload;
            })

            // MARK SENT: one action, manually sent by the user
            .addCase(markActionSent.fulfilled, (state, action) => {
                replaceActionInDigest(state, action.payload);
            })
            .addCase(markActionSent.rejected, (state, action) => {
                state.error = action.payload;
            })

            // DRAFT: fills in a lazily-written body for one row.
            .addCase(draftAction.fulfilled, (state, action) => {
                replaceActionInDigest(state, action.payload);
            })

            // SEND NOW: one row, sent immediately from the user's own account.
            .addCase(sendActionNow.fulfilled, (state, action) => {
                replaceActionInDigest(state, action.payload);
                if (state.digest && action.payload) {
                    state.digest.counts.sent = state.digest.actions.filter(
                        (a) => a.status === 'sent' || a.status === 'manual_sent'
                    ).length;
                }
            })
            .addCase(sendActionNow.rejected, (state, action) => {
                state.error = action.payload;
            })

            // EXECUTE: spends credits on every approved fetch_contact row.
            // Deliberately does NOT splice actions into state itself — the
            // caller refetches the digest afterward so row statuses (sent/
            // failed) come straight from the DB rather than being guessed
            // from the summary shape.
            .addCase(executeDigest.pending, (state) => {
                state.executing = true;
                state.error = null;
            })
            .addCase(executeDigest.fulfilled, (state, action) => {
                state.executing = false;
                state.lastExecuteSummary = action.payload;
            })
            .addCase(executeDigest.rejected, (state, action) => {
                state.executing = false;
                state.error = action.payload;
            })

            // REGENERATE
            .addCase(regenerateDigest.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(regenerateDigest.fulfilled, (state, action) => {
                state.loading = false;
                state.digest = action.payload;
            })
            .addCase(regenerateDigest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // SETTINGS
            .addCase(fetchAgentSettings.fulfilled, (state, action) => {
                const { settings, creditsBalance, channels } = action.payload || {};
                state.settings = settings || null;
                if (creditsBalance !== undefined) state.creditsBalance = creditsBalance;
                if (channels !== undefined) state.channels = channels;
            })
            .addCase(fetchAgentSettings.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(saveAgentSettings.fulfilled, (state, action) => {
                const { settings, creditsBalance, channels } = action.payload || {};
                state.settings = settings || state.settings;
                if (creditsBalance !== undefined) state.creditsBalance = creditsBalance;
                if (channels !== undefined) state.channels = channels;
            })
            .addCase(saveAgentSettings.rejected, (state, action) => {
                state.error = action.payload;
            });
    }
});

export default digestSlice.reducer;
